/**
 * Style Panel - Layout Element Styling Controls
 * 
 * Provides UI controls for styling layout elements (sections, rows, columns)
 * including padding, margins, backgrounds, borders, and alignment
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Grid,
} from '@mui/material';
import { ColorPicker } from './ColorPicker';
import {
  ExpandMore as ExpandMoreIcon,
  BorderAll as BorderIcon,
  Palette as PaletteIcon,
  Opacity as OpacityIcon,
  AspectRatio as DimensionIcon,
  SpaceBar as SpaceIcon,
} from '@mui/icons-material';

import { Style } from '../../types/page-builder.types';

interface StylePanelProps {
  elementType: 'section' | 'row' | 'column' | 'block';
  elementId: string;
  style: Style | undefined;
  onUpdateStyle: (style: Style) => void;
  readonly?: boolean;
}

export const StylePanel: React.FC<StylePanelProps> = ({
  elementType,
  elementId,
  style = {},
  onUpdateStyle,
  readonly = false
}) => {
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(
    new Set([]) // All style subsections collapsed by default for consistency
  );

  const togglePanel = useCallback((panel: string) => {
    setExpandedPanels(prev => {
      const next = new Set(prev);
      if (next.has(panel)) {
        next.delete(panel);
      } else {
        next.add(panel);
      }
      return next;
    });
  }, []);

  const updateStyle = useCallback((updates: Partial<Style>) => {
    onUpdateStyle({ ...style, ...updates });
  }, [style, onUpdateStyle]);

  const updateBoxShadow = useCallback((
    property: 'horizontal' | 'vertical' | 'blur' | 'spread' | 'color' | 'opacity',
    value: number | string
  ) => {
    const current = parseBoxShadow(style.boxShadow);

    if (property === 'opacity') {
      // Update opacity in the color
      const color = current.color;
      const rgb = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgb) {
        current.color = `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${value})`;
      } else {
        // Default to black with new opacity
        current.color = `rgba(0, 0, 0, ${value})`;
      }
    } else if (property === 'color') {
      // Preserve opacity when changing color
      const newColor = value as string;
      if (newColor.startsWith('rgba')) {
        current.color = newColor;
      } else if (newColor.startsWith('rgb')) {
        // Convert rgb to rgba with current opacity
        current.color = newColor.replace('rgb', 'rgba').replace(')', `, ${current.opacity})`);
      } else if (newColor.startsWith('#')) {
        // Convert hex to rgba with current opacity
        const hex = newColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        current.color = `rgba(${r}, ${g}, ${b}, ${current.opacity})`;
      } else {
        current.color = newColor;
      }
    } else {
      current[property] = value as number;
    }

    const boxShadow = `${current.horizontal}px ${current.vertical}px ${current.blur}px ${current.spread}px ${current.color}`;
    updateStyle({ boxShadow });
  }, [style.boxShadow, updateStyle]);

  const updateSpacing = useCallback((
    type: 'padding' | 'margin',
    direction: 'top' | 'right' | 'bottom' | 'left' | 'all',
    value: number
  ) => {
    const currentSpacing = style[type];
    
    if (direction === 'all') {
      updateStyle({ [type]: value });
    } else {
      const spacingObj = typeof currentSpacing === 'object' ? currentSpacing : {};
      updateStyle({
        [type]: {
          ...spacingObj,
          [direction]: value
        }
      });
    }
  }, [style, updateStyle]);

  const getSpacingValue = (
    type: 'padding' | 'margin',
    direction: 'top' | 'right' | 'bottom' | 'left' | 'all'
  ): number => {
    const spacing = style[type];
    if (typeof spacing === 'number') {
      // If spacing is a number, it applies to all directions
      return spacing;
    }
    if (typeof spacing === 'object' && spacing) {
      // If spacing is an object, get the specific direction value
      if (direction === 'all') {
        // For 'all', check if all values are the same
        const values = [spacing.top || 0, spacing.right || 0, spacing.bottom || 0, spacing.left || 0];
        const allSame = values.every(v => v === values[0]);
        return allSame ? values[0] : 0;
      }
      return spacing[direction] || 0;
    }
    return 0;
  };

  const getElementLabel = () => {
    switch (elementType) {
      case 'section': return 'Section';
      case 'row': return 'Row';
      case 'column': return 'Column';
      default: return 'Element';
    }
  };

  if (readonly) {
    return (
      <Paper sx={{ 
        width: 320, 
        borderRadius: 0, 
        borderLeft: 1, 
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="body2" color="text.secondary">
          Styling controls not available in preview mode
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ 
      width: 320, 
      borderRadius: 0, 
      borderLeft: 1, 
      borderColor: 'divider',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Style Properties
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Chip 
            label={`${getElementLabel()} Styles`} 
            color="primary" 
            variant="outlined" 
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" display="block" color="text.secondary">
            ID: {elementId}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        minHeight: 0
      }}>
        <Box sx={{ p: 1 }}>
          
          {/* Spacing Panel */}
          <Accordion 
            expanded={expandedPanels.has('spacing')} 
            onChange={() => togglePanel('spacing')}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SpaceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1">Spacing</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Padding */}
                <Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Padding (px)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Top"
                        type="number"
                        value={getSpacingValue('padding', 'top')}
                        onChange={(e) => updateSpacing('padding', 'top', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Right"
                        type="number"
                        value={getSpacingValue('padding', 'right')}
                        onChange={(e) => updateSpacing('padding', 'right', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bottom"
                        type="number"
                        value={getSpacingValue('padding', 'bottom')}
                        onChange={(e) => updateSpacing('padding', 'bottom', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Left"
                        type="number"
                        value={getSpacingValue('padding', 'left')}
                        onChange={(e) => updateSpacing('padding', 'left', Number(e.target.value))}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Margin */}
                <Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Margin (px)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Top"
                        type="number"
                        value={getSpacingValue('margin', 'top')}
                        onChange={(e) => updateSpacing('margin', 'top', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Right"
                        type="number"
                        value={getSpacingValue('margin', 'right')}
                        onChange={(e) => updateSpacing('margin', 'right', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bottom"
                        type="number"
                        value={getSpacingValue('margin', 'bottom')}
                        onChange={(e) => updateSpacing('margin', 'bottom', Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Left"
                        type="number"
                        value={getSpacingValue('margin', 'left')}
                        onChange={(e) => updateSpacing('margin', 'left', Number(e.target.value))}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Background Panel */}
          <Accordion 
            expanded={expandedPanels.has('background')} 
            onChange={() => togglePanel('background')}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PaletteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1">Background</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Background Color */}
                <ColorPicker
                  label="Background Color"
                  value={style.backgroundColor || style.bg || ''}
                  onChange={(color) => updateStyle({ backgroundColor: color })}
                  placeholder="#ffffff or transparent"
                />

                {/* Background Image options hidden for now */}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Border Panel */}
          <Accordion 
            expanded={expandedPanels.has('border')} 
            onChange={() => togglePanel('border')}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BorderIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1">Border</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Border Width"
                      type="number"
                      value={style.border?.width || 0}
                      onChange={(e) => updateStyle({
                        border: {
                          ...style.border,
                          width: Number(e.target.value)
                        }
                      })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Border Style</InputLabel>
                      <Select
                        value={style.border?.style || 'solid'}
                        label="Border Style"
                        onChange={(e) => updateStyle({
                          border: {
                            ...style.border,
                            style: e.target.value as any
                          }
                        })}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="solid">Solid</MenuItem>
                        <MenuItem value="dashed">Dashed</MenuItem>
                        <MenuItem value="dotted">Dotted</MenuItem>
                        <MenuItem value="double">Double</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Border Color */}
                <ColorPicker
                  label="Border Color"
                  value={style.border?.color || ''}
                  onChange={(color) => updateStyle({
                    border: {
                      ...style.border,
                      color: color
                    }
                  })}
                  placeholder="#cccccc or transparent"
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Border Radius"
                  type="number"
                  value={style.borderRadius || 0}
                  onChange={(e) => updateStyle({ borderRadius: Number(e.target.value) })}
                  helperText="Rounded corners in pixels"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Dimensions Panel */}
          <Accordion 
            expanded={expandedPanels.has('dimensions')} 
            onChange={() => togglePanel('dimensions')}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DimensionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1">Dimensions</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Min Height"
                      type="number"
                      value={style.minHeight || 0}
                      onChange={(e) => updateStyle({ minHeight: Number(e.target.value) })}
                      helperText="pixels"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Max Height"
                      type="number"
                      value={style.maxHeight || 0}
                      onChange={(e) => updateStyle({ maxHeight: Number(e.target.value) })}
                      helperText="pixels"
                    />
                  </Grid>
                </Grid>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Width"
                      value={style.width || ''}
                      onChange={(e) => updateStyle({ width: e.target.value })}
                      helperText="px, %, auto"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Height"
                      value={style.height || ''}
                      onChange={(e) => updateStyle({ height: e.target.value })}
                      helperText="px, %, auto"
                    />
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Effects Panel */}
          <Accordion 
            expanded={expandedPanels.has('effects')} 
            onChange={() => togglePanel('effects')}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <OpacityIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle1">Effects</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                {/* Opacity */}
                <Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Opacity: {style.opacity ?? 1}
                  </Typography>
                  <Slider
                    value={style.opacity ?? 1}
                    onChange={(_, value) => updateStyle({ opacity: value as number })}
                    min={0}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 0.5, label: '50%' },
                      { value: 1, label: '100%' }
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  />
                </Box>

                {/* Box Shadow */}
                <Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Box Shadow
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Horizontal Offset */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Horizontal"
                        type="number"
                        value={parseBoxShadow(style.boxShadow).horizontal}
                        onChange={(e) => updateBoxShadow('horizontal', Number(e.target.value))}
                        helperText="X offset (px)"
                        inputProps={{ step: 1 }}
                      />
                    </Grid>

                    {/* Vertical Offset */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Vertical"
                        type="number"
                        value={parseBoxShadow(style.boxShadow).vertical}
                        onChange={(e) => updateBoxShadow('vertical', Number(e.target.value))}
                        helperText="Y offset (px)"
                        inputProps={{ step: 1 }}
                      />
                    </Grid>

                    {/* Blur Radius */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Blur"
                        type="number"
                        value={parseBoxShadow(style.boxShadow).blur}
                        onChange={(e) => updateBoxShadow('blur', Number(e.target.value))}
                        helperText="Blur (px)"
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Grid>

                    {/* Spread Radius */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Spread"
                        type="number"
                        value={parseBoxShadow(style.boxShadow).spread}
                        onChange={(e) => updateBoxShadow('spread', Number(e.target.value))}
                        helperText="Spread (px)"
                        inputProps={{ step: 1 }}
                      />
                    </Grid>
                  </Grid>

                  {/* Shadow Color */}
                  <Box sx={{ mt: 2 }}>
                    <ColorPicker
                      label="Shadow Color"
                      value={parseBoxShadow(style.boxShadow).color}
                      onChange={(color) => updateBoxShadow('color', color)}
                      placeholder="rgba(0,0,0,0.2)"
                    />
                  </Box>

                  {/* Opacity Slider for Shadow */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Shadow Opacity: {Math.round(parseBoxShadow(style.boxShadow).opacity * 100)}%
                    </Typography>
                    <Slider
                      value={parseBoxShadow(style.boxShadow).opacity}
                      onChange={(_, value) => updateBoxShadow('opacity', value as number)}
                      min={0}
                      max={1}
                      step={0.05}
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 0.5, label: '50%' },
                        { value: 1, label: '100%' }
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                      size="small"
                    />
                  </Box>

                  {/* Quick Presets */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Quick Presets:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updateStyle({ boxShadow: '' })}
                      >
                        None
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updateStyle({ boxShadow: '0 1px 3px rgba(0,0,0,0.12)' })}
                      >
                        Subtle
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updateStyle({ boxShadow: '0 4px 6px rgba(0,0,0,0.1)' })}
                      >
                        Medium
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => updateStyle({ boxShadow: '0 10px 25px rgba(0,0,0,0.15)' })}
                      >
                        Large
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

        </Box>
      </Box>

      {/* Reset Button */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => onUpdateStyle({})}
          disabled={Object.keys(style).length === 0}
        >
          Reset All Styles
        </Button>
      </Box>
    </Paper>
  );
};

/**
 * Parse CSS box-shadow string into individual components
 */
function parseBoxShadow(boxShadow: string | undefined): {
  horizontal: number;
  vertical: number;
  blur: number;
  spread: number;
  color: string;
  opacity: number;
} {
  // Default values
  const defaults = {
    horizontal: 0,
    vertical: 0,
    blur: 0,
    spread: 0,
    color: 'rgba(0, 0, 0, 0.2)',
    opacity: 0.2
  };

  if (!boxShadow || boxShadow.trim() === '') {
    return defaults;
  }

  try {
    // Parse box-shadow: "0px 4px 6px 0px rgba(0, 0, 0, 0.1)"
    // Pattern: horizontal vertical blur spread color
    const regex = /(-?\d+(?:\.\d+)?px)\s+(-?\d+(?:\.\d+)?px)\s+(-?\d+(?:\.\d+)?px)\s+(-?\d+(?:\.\d+)?px)\s+(rgba?\([^)]+\)|#[0-9a-fA-F]{3,6}|[a-z]+)/;
    const match = boxShadow.match(regex);

    if (match) {
      const [, h, v, b, s, c] = match;

      // Extract opacity from rgba color
      let opacity = 0.2;
      const opacityMatch = c.match(/rgba?\([^,]+,[^,]+,[^,]+,\s*([0-9.]+)\)/);
      if (opacityMatch) {
        opacity = parseFloat(opacityMatch[1]);
      }

      return {
        horizontal: parseFloat(h),
        vertical: parseFloat(v),
        blur: parseFloat(b),
        spread: parseFloat(s),
        color: c,
        opacity
      };
    }

    // Try simpler format without spread: "0px 4px 6px rgba(0, 0, 0, 0.1)"
    const simpleRegex = /(-?\d+(?:\.\d+)?px)\s+(-?\d+(?:\.\d+)?px)\s+(-?\d+(?:\.\d+)?px)\s+(rgba?\([^)]+\)|#[0-9a-fA-F]{3,6}|[a-z]+)/;
    const simpleMatch = boxShadow.match(simpleRegex);

    if (simpleMatch) {
      const [, h, v, b, c] = simpleMatch;

      let opacity = 0.2;
      const opacityMatch = c.match(/rgba?\([^,]+,[^,]+,[^,]+,\s*([0-9.]+)\)/);
      if (opacityMatch) {
        opacity = parseFloat(opacityMatch[1]);
      }

      return {
        horizontal: parseFloat(h),
        vertical: parseFloat(v),
        blur: parseFloat(b),
        spread: 0,
        color: c,
        opacity
      };
    }
  } catch (error) {
    console.warn('Failed to parse box-shadow:', boxShadow, error);
  }

  return defaults;
}

export default StylePanel;